import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './ResyRewardsIntro.scss';

const STACK_CYCLE_MS = 1500;

const CardAnimationState = {
  EXPANDING: 'EXPANDING',
  COLLAPSING: 'COLLAPSING',
};

const ANIMATION_STATE_DURATIONS_MS = {
  [CardAnimationState.COLLAPSING]: 1500,
  [CardAnimationState.EXPANDING]: 1500,
};

const prefersReducedMotion = () => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getCardSummary = (card) => [
  `${card.eligibleLabel} ${card.eligiblePoints}`,
  `${card.resyLabel} ${card.resyPoints}`,
  `${card.totalLabel} ${card.totalPoints}`,
].join(', ');

const getCardImageName = ({ imageName }, matchMedia) => {
  const { sm, md, lg } = imageName || {};
  if (matchMedia.lg) return lg || md || sm;
  if (matchMedia.md) return md || sm || lg;
  return sm || md || lg;
};

const ResyRewardsIntro = ({ introConfig, matchMedia, urlPrefix }) => {
  const {
    eyebrow,
    title,
    copy,
    points = {},
  } = introConfig || {};

  const {
    icon,
    title: pointsTitle,
    copy: pointsCopy,
  } = points;

  const cardsData = introConfig?.points?.cards?.length > 0 ? introConfig?.points?.cards : [];
  const [activeFrameIndex, setActiveFrameIndex] = useState(0);
  const [cardFrameIndex, setCardFrameIndex] = useState(0);
  const [animationState, setAnimationState] = useState(CardAnimationState.EXPANDING);

  const activeCard = cardsData[cardFrameIndex] || cardsData[0];
  const isAnimated = cardsData.length > 1 && !prefersReducedMotion();

  const getCardPositionClass = (cardIndex) => {
    const relativePosition = (cardIndex - activeFrameIndex + cardsData.length) % cardsData.length;
    const { className } = cardsData[activeFrameIndex];
    let position = 'back';
    if (relativePosition === 0) position = 'front';
    if (relativePosition === 1) position = 'middle';
    if (animationState === CardAnimationState.EXPANDING) {
      return `${className}-${position}-expanding`;
    } if (animationState === CardAnimationState.COLLAPSING) {
      return `${className}-${position}-collapsing`;
    }
    return `${className}-${position}-expanding`;
  };

  useEffect(() => {
    if (!isAnimated) return undefined;
    const cardTimeout = setTimeout(() => {
      setCardFrameIndex((activeFrameIndex + 1) % cardsData.length);
    }, STACK_CYCLE_MS);

    return () => {
      clearTimeout(cardTimeout);
    };
  }, [activeFrameIndex]);

  useEffect(() => {
    if (!isAnimated) return undefined;
    const delay = ANIMATION_STATE_DURATIONS_MS[animationState] || STACK_CYCLE_MS;
    const animationTimeout = setTimeout(() => {
      setAnimationState((prevAnimationState) => {
        if (prevAnimationState === CardAnimationState.EXPANDING) {
          return CardAnimationState.COLLAPSING;
        }
        setActiveFrameIndex((prevFrameIndex) => (prevFrameIndex + 1) % cardsData.length);
        return CardAnimationState.EXPANDING;
      });
    }, delay);

    return () => {
      clearTimeout(animationTimeout);
    };
  }, [animationState]);

  if (!title) return null;

  return (
    <section className="ResyRewardsIntro">
      <div className="container">
        <div className="ResyRewardsIntro__header">
          {eyebrow && <p className="ResyRewardsIntro__eyebrow">{eyebrow}</p>}
          <h2
            className="ResyRewardsIntro__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
          />
          {copy && (
            <p
              className="ResyRewardsIntro__copy"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizeString(copy) }}
            />
          )}
        </div>
        {pointsTitle && (
          <div className="ResyRewardsIntro__points">
            <div className="ResyRewardsIntro__points-heading">
              {icon && (
                <img
                  className="ResyRewardsIntro__points-icon"
                  src={`${urlPrefix}${icon}`}
                  alt=""
                />
              )}
              <h3
                className="ResyRewardsIntro__points-title"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeString(pointsTitle) }}
              />
            </div>
            {cardsData.length > 0 && (
              <div
                className={cx('ResyRewardsIntro__cards')}
                aria-hidden="true"
              >
                {cardsData.map((card, index) => (
                  <img
                    key={card.className}
                    className={`ResyRewardsIntro__card ResyRewardsIntro__card--${getCardPositionClass(index)}`}
                    src={`${urlPrefix}${getCardImageName(card, matchMedia)}`}
                    alt=""
                  />
                ))}
              </div>
            )}
            <div className="ResyRewardsIntro__points-details">
              {pointsCopy && (
                <p
                  className="ResyRewardsIntro__points-copy"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeString(pointsCopy) }}
                />
              )}
              {cardsData.length > 0 && (
                <ul className="ResyRewardsIntro__sr-only">
                  {cardsData.map((card) => (
                    <li key={card.className}>{`${card.cardName}: ${getCardSummary(card)}`}</li>
                  ))}
                </ul>
              )}
              {activeCard && (
                // Rotates on a timer, so screen readers get the static list above instead.
                <div className="ResyRewardsIntro__breakdown" aria-hidden="true">
                  <div className="ResyRewardsIntro__breakdown-content" key={activeCard.cardName}>
                    <div className="ResyRewardsIntro__breakdown-header">
                      <img
                        className="ResyRewardsIntro__breakdown-icon"
                        src={`${urlPrefix}${getCardImageName(activeCard, matchMedia)}`}
                        alt=""
                      />
                      <span className="ResyRewardsIntro__breakdown-name">{activeCard.cardName}</span>
                    </div>
                    <dl className="ResyRewardsIntro__breakdown-list">
                      <div className="ResyRewardsIntro__breakdown-row">
                        <dt>{activeCard.eligibleLabel}</dt>
                        <dd>{activeCard.eligiblePoints}</dd>
                      </div>
                      <div className="ResyRewardsIntro__breakdown-row">
                        <dt>{activeCard.resyLabel}</dt>
                        <dd>{activeCard.resyPoints}</dd>
                      </div>
                      <div className="ResyRewardsIntro__breakdown-row ResyRewardsIntro__breakdown-row--total">
                        <dt>{activeCard.totalLabel}</dt>
                        <dd>{activeCard.totalPoints}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

ResyRewardsIntro.propTypes = {
  introConfig: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    copy: PropTypes.string,
    points: PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      copy: PropTypes.string,
      cards: PropTypes.arrayOf(PropTypes.shape({
        imageName: PropTypes.shape({
          sm: PropTypes.string,
          md: PropTypes.string,
          lg: PropTypes.string,
        }).isRequired,
        className: PropTypes.string.isRequired,
        cardName: PropTypes.string,
        eligibleLabel: PropTypes.string,
        eligiblePoints: PropTypes.string,
        resyLabel: PropTypes.string,
        resyPoints: PropTypes.string,
        totalLabel: PropTypes.string,
        totalPoints: PropTypes.string,
      })),
    }),
  }),
  matchMedia: PropTypes.shape({
    md: PropTypes.bool,
    lg: PropTypes.bool,
  }).isRequired,
  urlPrefix: PropTypes.string.isRequired,
};

ResyRewardsIntro.defaultProps = {
  introConfig: null,
};

export default ResyRewardsIntro;
